import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader, PageContainer } from '@/components/layout/PageHeader'
import { Button, Badge, Card, CardContent } from '@/components/ui'
import { adminApi } from '@/modules/admin/api/admin.api'
import type { AdminPermission } from '@/modules/admin/api/admin.api'
import { ROUTES } from '@/router/routes'

// Groupes de ressources pour affichage organisé
const RESOURCE_LABELS: Record<string, string> = {
  users: 'Utilisateurs', rbac: 'Rôles & Permissions', accounting: 'Comptabilité',
  treasury: 'Trésorerie', hr: 'Ressources Humaines', payroll: 'Paie',
  leaves: 'Congés', attendance: 'Pointage', projects: 'Projets',
  tasks: 'Tâches', chat: 'Chat', documents: 'Documents',
  meetings: 'Réunions', crm: 'CRM', tickets: 'Tickets',
  reports: 'Rapports', config: 'Configuration', audit: 'Audit',
}

const ACTION_COLORS: Record<string, string> = {
  read: 'bg-[var(--blue-bg)] text-[var(--blue-d)] border-[rgba(245,124,0,0.25)]',
  write: 'bg-[var(--green-bg)] text-[var(--green-text)] border-[rgba(16,185,129,0.25)]',
  delete: 'bg-[var(--red-bg)] text-[var(--red-text)] border-[rgba(239,68,68,0.25)]',
  validate: 'bg-[var(--mauve-bg)] text-[var(--mauve)] border-[rgba(139,92,246,0.25)]',
  admin: 'bg-[#FFF7ED] text-[var(--amber-text)] border-[rgba(249,115,22,0.25)]',
}

const ACTION_LABELS: Record<string, string> = {
  view: 'Voir', create: 'Créer', update: 'Modifier', delete: 'Supprimer',
  manage: 'Gérer', approve: 'Approuver', reject: 'Rejeter', cancel: 'Annuler',
  export: 'Exporter', import: 'Importer', validate: 'Valider', validate_entry: 'Valider écriture', archive: 'Archiver',
  restore: 'Restaurer', assign: 'Assigner', start: 'Démarrer', complete: 'Terminer',
  configure: 'Configurer', view_all: 'Voir tout', view_own: 'Voir les siens',
  close: 'Clôturer', issue: 'Émettre', send: 'Envoyer', pay: 'Payer',
  read: 'Lire', write: 'Écrire', admin: 'Administrer', '*': 'Tous droits',
}


export default function RoleDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState('')

  const { data: role, isLoading: roleLoading } = useQuery({
    queryKey: ['admin-role', id],
    queryFn: () => adminApi.getRoleById(id!),
    enabled: !!id,
  })

  const { data: allPermissions } = useQuery({
    queryKey: ['admin-permissions'],
    queryFn: () => adminApi.getPermissions(),
  })

  const updateRole = useMutation({
    mutationFn: () => adminApi.updateRole(id!, { name, description }),
    onSuccess: () => { setEditing(false); queryClient.invalidateQueries({ queryKey: ['admin-role', id] }) },
    onError: (err: any) => setError(err?.response?.data?.message ?? 'Erreur'),
  })

  // Initialiser les champs d'édition quand le rôle est chargé
  useEffect(() => {
    if (role) {
      setName((role as any).name ?? '')
      setDescription((role as any).description ?? '')
    }
  }, [role])

  const togglePermission = useMutation({
    mutationFn: ({ permId, add }: { permId: string; add: boolean }) =>
      add
        ? adminApi.assignPermissionsToRole(id!, [permId])
        : adminApi.removePermissionsFromRole(id!, [permId]),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-role', id] }),
  })

  if (roleLoading) return (
    <PageContainer>
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--blue)] border-t-transparent rounded-full" />
      </div>
    </PageContainer>
  )

  if (!role) return (
    <PageContainer><div className="text-center py-10 text-[var(--tx3)]">Rôle introuvable.</div></PageContainer>
  )

  // Permissions actuellement assignées à ce rôle
  // FIX: backend retourne rolePermissions[].permission, pas permissions[]
  const assignedIds = new Set<string>(
    (role as any).rolePermissions?.map((rp: any) => rp.permission?.id ?? rp.permissionId) ?? []
  )

  // FIX: getPermissions retourne { module: [Permission] } déjà groupé
  const grouped = (() => {
    if (!allPermissions) return {}
    // Si c'est un objet groupé (réponse backend getPermissionsByResource)
    if (!Array.isArray(allPermissions)) return allPermissions as Record<string, AdminPermission[]>
    // Fallback si tableau
    return (allPermissions as any[]).reduce<Record<string, AdminPermission[]>>((acc, perm) => {
      const key = perm.resource ?? perm.module ?? 'other'
      if (!acc[key]) acc[key] = []
      acc[key].push(perm)
      return acc
    }, {})
  })()

  return (
    <PageContainer>
      <PageHeader
        title={role.name}
        description={role.description ?? 'Rôle personnalisé'}
        breadcrumb={[
          { label: 'Administration' },
          { label: 'Rôles', href: ROUTES.ADMIN_ROLES },
          { label: role.name },
        ]}
        actions={
          <div className="flex items-center gap-2">
            {role.name !== 'SUPER_ADMIN' && !editing && (
              <Button size="sm" onClick={() => setEditing(true)}>Modifier</Button>
            )}
            {editing && (
              <>
                <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Annuler</Button>
                <Button size="sm" onClick={() => updateRole.mutate()} isLoading={updateRole.isPending}>Enregistrer</Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={() => navigate(ROUTES.ADMIN_ROLES)}>← Retour</Button>
          </div>
        }
      />

      {/* Infos rôle */}
      <Card className="mb-6">
        <CardContent className="py-5">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              {editing ? (
                <div className="space-y-3">
                  {error && <div className="bg-[var(--red-bg)] border border-[rgba(239,68,68,0.25)] rounded-lg p-3 text-sm text-[var(--red-text)]">{error}</div>}
                  <div>
                    <label className="block text-xs font-medium text-[var(--tx3)] mb-1">Nom</label>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value.toUpperCase().replace(/\s+/g, '_'))}
                      className="w-full bg-white border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500 uppercase"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--tx3)] mb-1">Description</label>
                    <input
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="w-full bg-white border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-semibold text-[var(--tx)]">{role.name}</h2>
                  <Badge variant={role.isSystem ? 'purple' : 'info'} size="sm">
                    {role.isSystem ? 'Système' : 'Personnalisé'}
                  </Badge>
                </div>
              )}
              {!editing && role.description && <p className="text-sm text-[var(--tx3)] mt-1">{role.description}</p>}
            </div>
            <div className="text-right text-sm text-[var(--tx3)]">
              <p><span className="font-semibold text-[var(--tx)]">{assignedIds.size}</span> permissions</p>
              <p className="text-xs text-[var(--tx3)] mt-0.5">{role._count?.userRoles ?? 0} utilisateurs</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Matrice permissions */}
      <Card>
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-[var(--bd)]">
            <h3 className="text-sm font-semibold text-[var(--tx2)]">Permissions assignées</h3>
            <p className="text-xs text-[var(--tx3)] mt-0.5">
              {(role as any).name === 'SUPER_ADMIN' ? 'Les permissions du rôle SUPER_ADMIN ne peuvent pas être modifiées.' : 'Cochez les permissions à accorder à ce rôle.'}
            </p>
          </div>

          <div className="divide-y divide-[var(--bd)]/50">
            {Object.entries(grouped).map(([resource, perms]) => (
              <div key={resource} className="px-5 py-4">
                <h4 className="text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-3">
                  {RESOURCE_LABELS[resource] ?? resource}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {perms.map((perm) => {
                    const isAssigned = assignedIds.has(perm.id)
                    const actionColor = ACTION_COLORS[perm.action] ?? 'bg-[var(--surface-2)] text-[var(--tx2)] border-[var(--bd2)]'
                    return (
                      <button
                        key={perm.id}
                        disabled={(role as any).name === 'SUPER_ADMIN' || togglePermission.isPending}
                        onClick={() => (role as any).name !== 'SUPER_ADMIN' && togglePermission.mutate({ permId: perm.id, add: !isAssigned })}
                        title={perm.description}
                        className={`
                          inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium
                          transition-all duration-150
                          ${isAssigned ? actionColor : 'bg-[var(--surface-2)] text-[var(--tx4)] border-[var(--bd)]/50'}
                          ${(role as any).name !== 'SUPER_ADMIN' ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}
                        `}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${isAssigned ? 'bg-current' : 'bg-[var(--surface-3)]'}`} />
                        {ACTION_LABELS[perm.action] ?? perm.action}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}

            {Object.keys(grouped).length === 0 && (
              <div className="py-12 text-center text-[var(--tx3)] text-sm">
                Chargement des permissions...
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </PageContainer>
  )
}
